/**
 * Field factory helpers for the ServiceFlowConfigurator.
 * Builds DOM elements for each supported input type, plus the shared
 * help-text span appended next to labels.
 *
 * Each `create*Field` function expects a `context` object with the shape:
 *   { fields: Array }
 * `context.fields` is mutated (pushed to) as fields are created, matching
 * the previous behavior of ServiceFlowConfigurator.fields.
 */
import { inputTemplateIDs } from './inputTemplateIDs';
import { createIcon } from '../../../icons';
import { createDtpSelect } from './dtpSelect';
import { createDtpComboboxSelect, createDtpComboboxMultiselect } from './dtpCombobox';
import { Datepicker } from '../../../../datepicker';
import { buildPatientDataJson, PATIENT_DATA_PROPERTY_BY_TOKEN } from '../../utils/patient-data';
import { getDefaultDatepickerSetup } from './inputsSetups';
import { checkDateValidity, fetchProductData, formatDate } from '../../../../../Foundation/utils';
import { getAccessoriesByType } from './accessories';

// Sets the label's visible text to the field's display name, appending a
// "*" right after it (same text node, separated by a normal space) when the
// field is required. Kept as a single flex item so the label's `gap` only
// applies between this text and the (separate) help icon.
function createRequiredIndicator() {
  const requiredIndicator = document.createElement('span');
  requiredIndicator.className = 'dtp-required-indicator';
  requiredIndicator.setAttribute('aria-hidden', 'true');
  requiredIndicator.textContent = ' *';
  return requiredIndicator;
}

function setLabelText(labelElement, field) {
  const labelText = document.createElement('span');
  labelText.className = 'dtp-label-text';
  labelText.textContent = field.DisplayName || field.Name;

  if (field.IsRequired) {
    labelText.appendChild(createRequiredIndicator());
  }

  labelElement.appendChild(labelText);
}

// include the serial number to stay unique across cards.
function buildInputName(prefix, field, context) {
  const serialNumber = String(context?.serialNumber ?? '').replace(/[^\w-]/g, '_');
  const fieldId = String(field.Id).replace(/[{}]/g, '');
  return serialNumber ? `${prefix}-${serialNumber}-${fieldId}` : `${prefix}-${fieldId}`;
}

// Creates the (initially hidden) error message element rendered below a
// .dtp-input, and links it to the input via aria-describedby.
function createErrorMessageElement(inputElement, inputName) {
  const errorMessage = document.createElement('span');
  errorMessage.className = 'dtp-input-error-message';
  errorMessage.id = inputName + '-error';
  errorMessage.setAttribute('role', 'alert');
  errorMessage.hidden = true;
  inputElement.setAttribute('aria-describedby', errorMessage.id);
  return errorMessage;
}

// Returns true when a single input is "empty" for error-highlighting
// purposes (mirrors the per-input checks in validation.js's isFieldEmpty).
function isInputEmpty(input) {
  if (!input) {
    return true;
  }
  if (input.type === 'checkbox') {
    return !input.checked;
  }
  return !input.value || !input.value.trim();
}

// Toggles the error state (border + message) on a field's input. For
// dtp-select/dtp-multiselect, the class goes on the visible
// .dtp-select__trigger instead of the hidden native <select>.
// field.errorInputs restricts which inputs get highlighted (e.g.
// category+reason multiselect only highlights the reason select).
// Pass an empty/falsy message to clear the error state.
export function setFieldError(field, message) {
  const inputElements = field.inputs;
  const errorElement = field.errorElement;
  if (!inputElements || !inputElements.length || !errorElement) {
    return;
  }

  const hasOverride = typeof field.isSelectionEmptyOverride === 'function';
  const overrideIsEmpty = hasOverride && field.isSelectionEmptyOverride();
  const highlightInputs = field.errorInputs || inputElements;

  inputElements.forEach((inputElement) => {
    const isHiddenNativeSelect = inputElement.classList.contains('dtp-select__native');
    const isFileInput = inputElement.type === 'file';
    const errorTargetElement = isHiddenNativeSelect
      ? inputElement.closest('.dtp-select')?.querySelector('.dtp-select__trigger') || inputElement
      : isFileInput
      ? inputElement.closest('.dtp-file-upload__dropzone') || inputElement
      : inputElement;

    const isEmpty = hasOverride ? overrideIsEmpty : isInputEmpty(inputElement);
    const shouldHighlight = message && isEmpty && highlightInputs.includes(inputElement);

    if (shouldHighlight) {
      errorTargetElement.classList.add('dtp-input--error');
      inputElement.setAttribute('aria-invalid', 'true');
    } else {
      errorTargetElement.classList.remove('dtp-input--error');
      inputElement.removeAttribute('aria-invalid');
    }
  });

  if (message) {
    errorElement.textContent = message;
    errorElement.hidden = false;
  } else {
    errorElement.textContent = '';
    errorElement.hidden = true;
  }
}

// Re-runs a field's "mandatory" error state right after its value changes,
// instead of waiting for the next blur/submit. Only active after a submit
// attempt (context.isSubmitted). Used by fields whose value changes don't
// fire 'blur' on their real input (category/reason multiselect, file dropzone).
function syncRequiredFieldError(field, context) {
  const isSubmitted =
    typeof context.isSubmitted === 'function' ? context.isSubmitted() : context.isSubmitted;
  if (!isSubmitted || !field.IsRequired) {
    setFieldError(field, '');
    return;
  }

  const isEmpty =
    typeof field.isSelectionEmptyOverride === 'function'
      ? field.isSelectionEmptyOverride()
      : (field.inputs || []).some((input) => isInputEmpty(input));

  if (isEmpty) {
    const message =
      field.customRequiredMessage ||
      (context.translations && context.translations.thisStepIsMandatory) ||
      'This field is mandatory';
    setFieldError(field, message);
  } else {
    setFieldError(field, '');
  }
}

function appendHelpText(containerElement, field) {
  const helpText = document.createElement('span');
  helpText.className = 'dtp-helptext';
  helpText.textContent = field.HelpText || '';
  helpText.hidden = !field.HelpText;
  containerElement.appendChild(helpText);
  return helpText;
}

// Creates a .dtp-field container with a <label> wired to `inputName`.
// Shared by the simple single-input fields (text, textarea, date, file).
function createFieldContainer(sectionContainer, field, inputName, extraContainerClasses) {
  const container = document.createElement('div');
  container.classList.add('dtp-field', ...(extraContainerClasses || []));
  sectionContainer.appendChild(container);

  const inputLabel = document.createElement('label');
  inputLabel.setAttribute('for', inputName);
  container.appendChild(inputLabel);
  setLabelText(inputLabel, field);

  return { container, inputLabel };
}

function createInputTextField(field, sectionContainer, context) {
  const inputName = buildInputName('textinput', field, context);
  const { container } = createFieldContainer(sectionContainer, field, inputName);

  const textInput = document.createElement('input');
  textInput.type = 'text';
  textInput.className = 'dtp-input';
  textInput.id = inputName;
  textInput.setAttribute('name', inputName);
  textInput.setAttribute('data-fieldid', field.Id);
  textInput.setAttribute('placeholder', field.Name);

  if (field.Text) {
    textInput.value = field.Text;
  }
  container.appendChild(textInput);

  const textInputError = createErrorMessageElement(textInput, inputName);
  appendHelpText(container, field);
  container.appendChild(textInputError);

  field.inputs = [textInput];
  field.errorElement = textInputError;
  field.container = container;
  context.fields.push(field);
  syncImpressionsFields(context);
}

function createInputTextAreaField(field, sectionContainer, context) {
  const inputName = buildInputName('textarea', field, context);
  const { container } = createFieldContainer(sectionContainer, field, inputName);

  const textArea = document.createElement('textarea');
  textArea.className = 'dtp-input';
  textArea.id = inputName;
  textArea.setAttribute('name', inputName);
  textArea.setAttribute('data-fieldid', field.Id);
  textArea.setAttribute('placeholder', field.Name);

  if (field.Text) {
    textArea.value = field.Text;
  }
  container.appendChild(textArea);

  const textAreaError = createErrorMessageElement(textArea, inputName);
  appendHelpText(container, field);
  container.appendChild(textAreaError);

  field.inputs = [textArea];
  field.errorElement = textAreaError;
  context.fields.push(field);
}

// Builds the shared "date" input UI: .dtp-datefield-container wrapping the
// text input with its calendar icon (kept as next sibling, as Datepicker
// expects). Used by createInputDateField and PatientInfo's date sub-fields.
function buildDtpDateInput(inputName, fieldId, options) {
  options = options || {};

  // Sitecore's ShortDateFormat uses lowercase tokens; Datepicker/formatDate
  // expect uppercase. Falls back to the default format if none is provided.
  const datepickerSetup = {
    ...getDefaultDatepickerSetup(),
    ...(options.dateFormat && { format: options.dateFormat.toUpperCase() }),
  };
  const datepickerFormat = datepickerSetup.format;

  const divContainer = document.createElement('div');
  divContainer.className = 'dtp-datefield-container';

  const dateInput = document.createElement('input');
  dateInput.type = 'text';
  dateInput.className = 'dtp-input dtp-datefield-input';
  dateInput.id = inputName;
  dateInput.setAttribute('name', inputName);
  dateInput.setAttribute('data-fieldid', fieldId);
  // Native placeholder: dtp-datefield-input's own padding leaves room for
  // the icon, so the browser shows/hides it automatically.
  dateInput.setAttribute('placeholder', datepickerFormat);
  dateInput.setAttribute('data-datepicker', JSON.stringify(datepickerSetup));

  // .NET's DateTime.MinValue ("0001-01-01T00:00:00") means "no value" -
  // treated as empty so the input shows its placeholder.
  if (checkDateValidity(options.dateValue)) {
    const parsedDate = new Date(Date.parse(options.dateValue));
    dateInput.value = formatDate(parsedDate, datepickerFormat);
  }
  divContainer.appendChild(dateInput);

  const dateIcon = createIcon('calendar', 'dtp-datefield-icon');
  divContainer.appendChild(dateIcon);

  return { container: divContainer, dateInput: dateInput };
}

function createInputDateField(field, sectionContainer, context) {
  const inputName = buildInputName('dateinput', field, context);
  const { container } = createFieldContainer(sectionContainer, field, inputName, ['dtp-datefield']);

  const dateField = buildDtpDateInput(inputName, field.Id, {
    dateValue: field.Date,
    dateFormat: context.dateFormat,
  });
  container.appendChild(dateField.container);

  const dateInputError = createErrorMessageElement(dateField.dateInput, inputName);
  appendHelpText(container, field);
  container.appendChild(dateInputError);

  field.inputs = [dateField.dateInput];
  field.errorElement = dateInputError;
  context.fields.push(field);

  new Datepicker(dateField.dateInput);
}

function createInputCheckboxField(field, sectionContainer, context) {
  const container = document.createElement('div');
  container.classList.add('dtp-field', 'dtp-checkbox');
  sectionContainer.appendChild(container);

  const checkboxLabel = document.createElement('label');
  container.appendChild(checkboxLabel);

  // Wraps the hidden native input with the custom visual box so
  // input:hover/:focus-visible/:checked can style .dtp-checkbox-box via
  // sibling selectors instead of JS.
  const checkboxVisual = document.createElement('span');
  checkboxVisual.className = 'dtp-checkbox-visual';
  checkboxLabel.appendChild(checkboxVisual);

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.className = 'dtp-checkbox-input';
  const inputName = buildInputName('checkbox', field, context);
  checkbox.id = inputName;
  checkbox.setAttribute('name', inputName);
  checkbox.setAttribute('data-fieldid', field.Id);

  if (field.Checked) {
    checkbox.checked = true;
  }
  checkboxVisual.appendChild(checkbox);

  const checkboxBox = document.createElement('span');
  checkboxBox.className = 'dtp-checkbox-box';
  checkboxBox.setAttribute('aria-hidden', 'true');

  const checkboxIcon = createIcon('checkbox-check', 'dtp-checkbox-icon');
  checkboxBox.appendChild(checkboxIcon);
  checkboxVisual.appendChild(checkboxBox);

  setLabelText(checkboxLabel, field);

  field.inputs = [checkbox];
  context.fields.push(field);
}

// Fields with DependentControlId/DependentControlValue (e.g. Impressions serial
// numbers and upload) are visible only when the controlling dropdown's value
// (NavisionValue, else FeatureKey, else Name - same as legacy) matches.
const normalizeDependencyId = (id) =>
  String(id || '')
    .replace(/[{}]/g, '')
    .toLowerCase();

function setImpressionsFieldActive(targetField, isActive) {
  if (!targetField?.container) {
    return;
  }
  targetField.IsRequired = isActive;
  targetField.dependency = { ...targetField.dependency, hidden: !isActive };
  targetField.container.hidden = !isActive;

  const labelText = targetField.container.querySelector('.dtp-label-text');
  const indicator = labelText?.querySelector('.dtp-required-indicator');
  if (labelText && isActive && !indicator) {
    labelText.appendChild(createRequiredIndicator());
  } else if (!isActive) {
    indicator?.remove();
  }
  if (!isActive) {
    setFieldError(targetField, '');
    (targetField.inputs || []).forEach((input) => {
      if (input.type === 'file' || input.type === 'text') {
        input.value = '';
        input.dispatchEvent(new Event('change'));
      }
    });
  }
}

function syncImpressionsFields(context) {
  const fields = context.fields || [];
  fields
    .filter((f) => f.DependentControlId && f.container)
    .forEach((dependent) => {
      const trigger = fields.find(
        (f) => normalizeDependencyId(f.Id) === normalizeDependencyId(dependent.DependentControlId),
      );
      if (!trigger?.getSubmitValue) {
        return;
      }
      setImpressionsFieldActive(
        dependent,
        trigger.getSubmitValue() === dependent.DependentControlValue,
      );
    });
}

function createDropDownField(field, sectionContainer, context) {
  const container = document.createElement('div');
  container.classList.add('dtp-field');
  sectionContainer.appendChild(container);

  const fieldLabel = document.createElement('label');
  const inputName = buildInputName('dropdown', field, context);
  const labelId = inputName + '-label';
  fieldLabel.id = labelId;
  fieldLabel.setAttribute('for', inputName + '-trigger');
  container.appendChild(fieldLabel);
  setLabelText(fieldLabel, field);

  const options = (field.ConfigurationFieldOptions || []).map((option) => ({
    id: option.Id,
    label: option.DisplayName,
  }));
  const selectedId = field.SelectedItem ? field.SelectedItem.Id : null;

  const selectTranslation = context.translations?.selectLabel ?? 'Select';
  const selectPlaceholder = `${selectTranslation} ${field.Name}`;

  const dtpSelect = createDtpSelect({
    id: inputName,
    options: options,
    placeholder: selectPlaceholder,
    selectedId: selectedId,
    labelId: labelId,
  });
  dtpSelect.nativeSelect.setAttribute('data-fieldid', field.Id);
  container.appendChild(dtpSelect.container);

  const selectError = createErrorMessageElement(dtpSelect.triggerElement, inputName);
  appendHelpText(container, field);
  container.appendChild(selectError);

  field.inputs = [dtpSelect.nativeSelect];
  field.errorElement = selectError;

  // NavisionValue, else FeatureKey, else Name.
  field.getSubmitValue = () => {
    const selectedOption = (field.ConfigurationFieldOptions || []).find(
      (option) => option.Id === dtpSelect.nativeSelect.value,
    );
    if (!selectedOption) {
      return '';
    }
    return selectedOption.NavisionValue || selectedOption.FeatureKey || selectedOption.Name;
  };

  dtpSelect.nativeSelect.addEventListener('change', () => {
    syncRequiredFieldError(field, context);
  });

  dtpSelect.nativeSelect.addEventListener('change', () => syncImpressionsFields(context));

  context.fields.push(field);
  syncImpressionsFields(context);
}

// Two-select "Category -> Reason" field. Reason's options are the selected
// category's Children (or all categories' Children combined if none is
// selected). Selected reasons render as removable tag chips below the row.
function createCategorizedMultiselectField(field, sectionContainer, context) {
  const container = document.createElement('div');
  container.classList.add('dtp-field');
  sectionContainer.appendChild(container);

  const fieldLabel = document.createElement('label');
  const inputName = buildInputName('categorizedmultiselect', field, context);
  const labelId = inputName + '-label';
  fieldLabel.id = labelId;
  container.appendChild(fieldLabel);
  setLabelText(fieldLabel, field);

  const selectRow = document.createElement('div');
  selectRow.className = 'dtp-categorized-select';
  container.appendChild(selectRow);

  const categories = field.ConfigurationFieldOptions || [];
  const categoryOptions = categories.map((category) => ({
    id: category.Id,
    label: category.DisplayName || category.Name,
  }));

  const categorySelect = createDtpComboboxSelect({
    id: inputName + '-category',
    options: categoryOptions,
    placeholder: field.EmptyCategoryLabel || 'Choose category',
    labelId: labelId,
  });
  categorySelect.container.classList.add('dtp-categorized-select__category');
  selectRow.appendChild(categorySelect.container);

  const reasonMultiselect = createDtpComboboxMultiselect({
    id: inputName + '-reason',
    options: [],
    placeholder: field.EmptyChildsLabel || 'Choose reasons',
    labelId: labelId,
    onChange: () => handleReasonMultiselectChange(),
  });
  reasonMultiselect.container.classList.add('dtp-categorized-select__reason');
  selectRow.appendChild(reasonMultiselect.container);

  const tagList = document.createElement('div');
  tagList.className = 'dtp-tag-list';
  container.appendChild(tagList);

  const reasonError = createErrorMessageElement(
    reasonMultiselect.nativeSelect,
    inputName + '-reason',
  );
  appendHelpText(container, field);
  container.appendChild(reasonError);

  function getSelectedCategory() {
    return categories.find((category) => category.Id === categorySelect.nativeSelect.value);
  }

  // Reason options for a category, or all categories' children combined
  // when none is selected.
  function getReasonOptionsForCategory(category) {
    const children = category
      ? category.Children || []
      : categories.reduce((allChildren, cat) => allChildren.concat(cat.Children || []), []);

    return children.map((child) => ({
      id: child.Id,
      label: child.DisplayName || child.Name,
    }));
  }

  // Finds which category a reason id belongs to.
  function findCategoryForReasonId(reasonId) {
    return categories.find((category) =>
      (category.Children || []).some((child) => child.Id === reasonId),
    );
  }

  // Selected reason ids across all categories.
  let allSelectedReasonIds = [];

  function getSelectedIdsForCategory(category) {
    return category
      ? allSelectedReasonIds.filter((id) =>
          (category.Children || []).some((child) => child.Id === id),
        )
      : allSelectedReasonIds;
  }

  function renderTagList() {
    tagList.innerHTML = '';

    allSelectedReasonIds.forEach((reasonId) => {
      const reasonCategory = findCategoryForReasonId(reasonId);
      const reason =
        reasonCategory && (reasonCategory.Children || []).find((child) => child.Id === reasonId);
      if (!reasonCategory || !reason) {
        return;
      }

      const tag = document.createElement('span');
      tag.className = 'dtp-tag';

      // Single flex item so category/reason only get a plain space between
      // them, not the tag's flex gap (reserved for the remove button).
      const tagLabel = document.createElement('span');

      const tagCategoryLabel = document.createElement('span');
      tagCategoryLabel.textContent = (reasonCategory.DisplayName || reasonCategory.Name) + ' ';
      tagLabel.appendChild(tagCategoryLabel);

      const tagReasonLabel = document.createElement('strong');
      tagReasonLabel.textContent = reason.DisplayName || reason.Name;
      tagLabel.appendChild(tagReasonLabel);

      tag.appendChild(tagLabel);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'dtp-tag__remove';
      const removeLabel = context.translations?.removeLabel ?? 'Remove';
      removeButton.setAttribute(
        'aria-label',
        removeLabel + ' ' + (reason.DisplayName || reason.Name),
      );
      const closeIcon = createIcon('close');
      removeButton.appendChild(closeIcon);
      removeButton.addEventListener('click', () => {
        allSelectedReasonIds = allSelectedReasonIds.filter((id) => id !== reason.Id);

        reasonMultiselect.setOptions(
          reasonMultiselect.getOptions(),
          getSelectedIdsForCategory(getSelectedCategory()),
        );
        renderTagList();
        syncRequiredFieldError(field, context);
        tagList.dispatchEvent(new Event('change', { bubbles: true }));
      });
      tag.appendChild(removeButton);

      tagList.appendChild(tag);
    });
  }

  function syncReasonSelectForCategory(selectedCategory) {
    const reasonOptions = getReasonOptionsForCategory(selectedCategory);
    reasonMultiselect.setOptions(reasonOptions, getSelectedIdsForCategory(selectedCategory));
    renderTagList();
    syncRequiredFieldError(field, context);
  }

  categorySelect.nativeSelect.addEventListener('change', () => {
    syncReasonSelectForCategory(getSelectedCategory());
  });

  // Reasons from non-active categories exist only as tags, so merge/unmerge uses these hooks.
  selectRow.dtpCategorizedState = {
    getReasonIds: () => [...allSelectedReasonIds],
    setReasonIds: (ids) => {
      allSelectedReasonIds = [...(ids || [])];
      syncReasonSelectForCategory(getSelectedCategory());
    },
  };

  function handleReasonMultiselectChange() {
    const selectedCategory = getSelectedCategory();
    if (selectedCategory) {
      const otherCategoryIds = allSelectedReasonIds.filter(
        (id) => !(selectedCategory.Children || []).some((child) => child.Id === id),
      );
      allSelectedReasonIds = otherCategoryIds.concat(reasonMultiselect.getSelectedIds());
    } else {
      allSelectedReasonIds = reasonMultiselect.getSelectedIds();
    }
    renderTagList();
    syncRequiredFieldError(field, context);
  }

  reasonMultiselect.setOptions(
    getReasonOptionsForCategory(getSelectedCategory()),
    allSelectedReasonIds,
  );

  if (Array.isArray(field.SelectedItem) && field.SelectedItem.length) {
    allSelectedReasonIds = field.SelectedItem.map((selectedReason) => selectedReason.Id);

    const preselectedCategory = categories.find((category) =>
      (category.Children || []).some((child) => allSelectedReasonIds.indexOf(child.Id) !== -1),
    );

    if (preselectedCategory) {
      categorySelect.nativeSelect.value = preselectedCategory.Id;
      syncReasonSelectForCategory(preselectedCategory);
    } else {
      reasonMultiselect.setOptions(getReasonOptionsForCategory(null), allSelectedReasonIds);
    }

    renderTagList();
  }

  field.inputs = [categorySelect.nativeSelect, reasonMultiselect.nativeSelect];
  field.errorElement = reasonError;
  // Required-ness is based on allSelectedReasonIds (across all categories),
  // not either select's own value.
  field.isSelectionEmptyOverride = () => allSelectedReasonIds.length === 0;
  field.getSubmitValue = () => [...allSelectedReasonIds].sort().join('|');
  field.errorInputs = [categorySelect.nativeSelect, reasonMultiselect.nativeSelect];
  context.fields.push(field);
}

// Builds the drag-and-drop file upload dropzone UI (dropzone, hidden file
// input, selected-file chip list). Callers append `dropZone`/`fileList`
// into their own container, so this is shared by fields with a visible
// label/error (createFileUploadField).
function buildDtpFileUploadDropzone(
  inputName,
  fieldId,
  translations,
  onFileChange,
  existingFileName,
) {
  const dropZone = document.createElement('div');
  dropZone.className = 'dtp-file-upload__dropzone';
  dropZone.setAttribute('role', 'button');
  dropZone.setAttribute('tabindex', '0');

  const uploadButton = document.createElement('span');
  uploadButton.className = 'dtp-file-upload__button';

  const uploadIcon = createIcon('upload', 'dtp-file-upload__icon');
  uploadButton.appendChild(uploadIcon);

  const uploadButtonText = document.createElement('span');
  uploadButtonText.textContent = translations?.dragOrUploadFileLabel ?? 'Drag or upload file';
  uploadButton.appendChild(uploadButtonText);

  dropZone.appendChild(uploadButton);

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.className = 'dtp-file-upload__input';
  fileInput.id = inputName;
  fileInput.setAttribute('name', inputName);
  fileInput.setAttribute('data-fieldid', fieldId);
  fileInput.hidden = true;
  dropZone.appendChild(fileInput);

  const fileList = document.createElement('div');
  fileList.className = 'dtp-file-upload__file-list';

  function renderFileChip(name) {
    const fileChip = document.createElement('div');
    fileChip.className = 'dtp-file-upload__file';

    const fileName = document.createElement('span');
    fileName.textContent = name;
    fileChip.appendChild(fileName);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'dtp-file-upload__file-remove';
    removeButton.setAttribute('aria-label', translations?.removeLabel ?? 'Remove');
    const closeIcon = createIcon('close');
    removeButton.appendChild(closeIcon);
    removeButton.addEventListener('click', () => {
      fileInput.value = '';
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
    fileChip.appendChild(removeButton);

    fileList.appendChild(fileChip);
  }

  function renderFile(file) {
    fileList.innerHTML = '';
    // Any user action (new file or remove) replaces the file restored from the cart.
    fileInput.keepExistingFile = false;

    if (typeof onFileChange === 'function') {
      onFileChange(file || null);
    }
    if (file) {
      renderFileChip(file.name);
    }
  }

  // Edit mode: file already stored
  // The input itself stays empty; submit sends samefile = true (see buildSubmitPayload.js).
  if (existingFileName) {
    fileInput.keepExistingFile = true;
    renderFileChip(existingFileName);
  }

  dropZone.addEventListener('click', () => {
    fileInput.click();
  });

  dropZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      fileInput.click();
    }
  });

  fileInput.addEventListener('change', () => {
    renderFile(fileInput.files ? fileInput.files[0] : null);
  });

  ['dragenter', 'dragover'].forEach((eventName) => {
    dropZone.addEventListener(eventName, (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropZone.classList.add('dtp-file-upload__dropzone--active');
    });
  });

  ['dragleave', 'drop'].forEach((eventName) => {
    dropZone.addEventListener(eventName, (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropZone.classList.remove('dtp-file-upload__dropzone--active');
    });
  });

  dropZone.addEventListener('drop', (e) => {
    const droppedFiles = e.dataTransfer && e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      fileInput.files = droppedFiles;
      renderFile(droppedFiles[0]);
    }
  });

  return { dropZone, fileInput, fileList };
}

// Shared core for file-upload fields: builds the dropzone/file-list,
// error element, and wires up field.inputs/errorElement/context.fields.
// Callers append dropZone/fileList/fileInputError into their own container.
function buildFileUploadFieldCore(field, context, inputName) {
  const { dropZone, fileInput, fileList } = buildDtpFileUploadDropzone(
    inputName,
    field.Id,
    context.translations,
    () => {
      syncRequiredFieldError(field, context);
    },
    field.FileName,
  );

  const fileInputError = createErrorMessageElement(fileInput, inputName);

  field.isSelectionEmptyOverride = () =>
    !fileInput.keepExistingFile && !(fileInput.files && fileInput.files.length > 0);
  field.inputs = [fileInput];
  field.errorElement = fileInputError;
  context.fields.push(field);

  return { dropZone, fileList, fileInputError };
}

function createFileUploadField(field, sectionContainer, context) {
  // The backend appends a download link (<a>) for the previously uploaded file to DisplayName.
  field.DisplayName = (field.DisplayName || '')
    .replace(/<a\b[^>]*>.*?<\/a>/gi, '')
    .replace(/\s*:\s*$/, '')
    .trim();

  const inputName = buildInputName('fileinput', field, context);
  const { container } = createFieldContainer(sectionContainer, field, inputName, [
    'dtp-file-upload',
  ]);

  const { dropZone, fileList, fileInputError } = buildFileUploadFieldCore(
    field,
    context,
    inputName,
  );
  container.appendChild(dropZone);
  container.appendChild(fileList);
  appendHelpText(container, field);
  container.appendChild(fileInputError);
  field.container = container;
  syncImpressionsFields(context);
}

// Renders the PatientInfo group: a plain text input for each entry in
// field.Fields. Sub-fields are flattened into context.fields individually
// (sharing the parent field's Id) so validateAllFields treats them like
// any other field. The group title is rendered at the section level.
function createPatientInfoField(field, sectionContainer, context) {
  const subFields = field.Fields || [];
  const firstSubFieldIndex = context.fields.length;
  const patientData = field.PatientData || {};

  subFields.forEach((subField) => {
    if (subField.IsVisible === false) {
      return;
    }

    const currentValue =
      patientData[PATIENT_DATA_PROPERTY_BY_TOKEN[subField.Token] || subField.Token] ?? null;

    const subFieldContainer = document.createElement('div');
    subFieldContainer.classList.add('dtp-field');
    if (subField.InputType === 'date') {
      subFieldContainer.classList.add('dtp-datefield');
    }
    sectionContainer.appendChild(subFieldContainer);

    const inputName = buildInputName('patientinfo', field, context) + '-' + subField.Token;

    const inputLabel = document.createElement('label');
    inputLabel.id = inputName + '-label';
    inputLabel.setAttribute(
      'for',
      subField.InputType === 'select' ? inputName + '-trigger' : inputName,
    );
    subFieldContainer.appendChild(inputLabel);
    setLabelText(inputLabel, {
      DisplayName: subField.Label,
      IsRequired: !subField.IsOptional,
    });

    let subFieldInput;

    if (subField.InputType === 'select') {
      const selectOptions = (subField.Options || []).map((option) => ({
        id: option.Value,
        label: option.Text,
      }));
      const selectTranslation = context.translations?.selectLabel ?? 'Select';
      const selectPlaceholder = `${selectTranslation} ${subField.Name ?? subField.Label}`;

      const dtpSelect = createDtpSelect({
        id: inputName,
        options: selectOptions,
        placeholder: selectPlaceholder,
        selectedId: currentValue !== null && currentValue !== '' ? String(currentValue) : null,
        labelId: inputLabel.id,
      });
      dtpSelect.nativeSelect.setAttribute('data-fieldid', field.Id);
      dtpSelect.nativeSelect.setAttribute('data-token', subField.Token);
      subFieldContainer.appendChild(dtpSelect.container);

      const selectError = createErrorMessageElement(dtpSelect.triggerElement, inputName);
      appendHelpText(subFieldContainer, subField);
      subFieldContainer.appendChild(selectError);

      subFieldInput = dtpSelect.nativeSelect;
      context.fields.push({
        Id: field.Id,
        DisplayName: subField.Label,
        IsRequired: !subField.IsOptional,
        inputs: [subFieldInput],
        errorElement: selectError,
      });
    } else if (subField.InputType === 'date') {
      const dateField = buildDtpDateInput(inputName, field.Id, {
        dateValue: currentValue,
        dateFormat: context.dateFormat,
      });
      dateField.dateInput.setAttribute('data-token', subField.Token);
      subFieldContainer.appendChild(dateField.container);

      const dateFieldError = createErrorMessageElement(dateField.dateInput, inputName);
      appendHelpText(subFieldContainer, subField);
      subFieldContainer.appendChild(dateFieldError);

      subFieldInput = dateField.dateInput;

      new Datepicker(subFieldInput);
      context.fields.push({
        Id: field.Id,
        DisplayName: subField.Label,
        IsRequired: !subField.IsOptional,
        inputs: [subFieldInput],
        errorElement: dateFieldError,
      });
    } else {
      subFieldInput = document.createElement('input');
      subFieldInput.type = 'text';
      subFieldInput.className = 'dtp-input';
      subFieldInput.id = inputName;
      subFieldInput.setAttribute('name', inputName);
      subFieldInput.setAttribute('data-fieldid', field.Id);
      subFieldInput.setAttribute('data-token', subField.Token);
      if (currentValue !== null) {
        subFieldInput.value = currentValue;
      }

      subFieldContainer.appendChild(subFieldInput);

      const subFieldInputError = createErrorMessageElement(subFieldInput, inputName);
      appendHelpText(subFieldContainer, subField);
      subFieldContainer.appendChild(subFieldInputError);
      context.fields.push({
        Id: field.Id,
        DisplayName: subField.Label,
        IsRequired: !subField.IsOptional,
        inputs: [subFieldInput],
        errorElement: subFieldInputError,
      });
    }
  });

  // The backend expects a single PatientDataBE JSON string for the whole group
  // (FieldsToConfiguratorMapper.SetDataOnPatientInfoField)
  const subFieldEntries = context.fields.slice(firstSubFieldIndex);
  subFieldEntries.forEach((entry, index) => {
    if (index > 0) {
      entry.excludeFromSubmit = true;
    }
  });

  if (subFieldEntries.length) {
    subFieldEntries[0].getSubmitValue = () => buildPatientDataJson(subFieldEntries);
  }
}

// Renders a Sitecore "Accessories" field: optional filter dropdowns
// (field.Filters, e.g. Receiver Length/Power) that narrow the accessories
// fetched from /api/accessories/{modelNo}/{accessoryType}, plus a final
// accessory picker. Matches the legacy configurator's filtering behavior
// (Feature/configurator/helpers/accessories.js).
async function createAccessoriesField(field, sectionContainer, context) {
  const inputName = buildInputName('accessoriesfield', field, context);
  const accessoryType = field.AccessoriesType;

  // Without a serialNumber there's no way to resolve a ModelNo, so skip the
  // network calls entirely instead of firing a request that can't succeed.
  if (!context.serialNumber) {
    return;
  }

  // Same data source the legacy configurator uses to resolve the current
  // product's ModelNo before fetching its accessories. Failures here must
  // not prevent the filter/picker selects below from rendering.
  let modelNo = context.modelNo || null;

  if (!modelNo) {
    try {
      // apiKey/userId/warrantyApiUrl come from ServiceFlowWarranty's
      // element.dataset (see ServiceFlowConfigurator.warrantyApiConfig).
      // serialNumber is already resolved and stored on the configurator.
      const { apiKey, userId, warrantyApiUrl } = context.warrantyApiConfig || {};
      const serialNumber = context.serialNumber;
      const productData = await fetchProductData({ apiKey, serialNumber, userId, warrantyApiUrl });
      modelNo = productData ? productData.modelNo : null;
    } catch (error) {
      console.error('Failed to resolve product data for accessories field', error);
    }
  }

  let allOptions = [];
  try {
    allOptions = (await getAccessoriesByType({ accessoryType, modelNo })) || [];
  } catch (error) {
    console.error('Failed to fetch accessories', error);
    allOptions = [];
  }

  // No accessories available (empty response or fetch failure): skip
  // rendering this field entirely, instead of showing filter/picker
  // dropdowns with no options.
  if (!allOptions.length) {
    return;
  }

  let filterValues = [];
  let filteredAccessories = allOptions;

  function getAccessoryOptions(accessories) {
    return accessories.map((accessory) => ({ id: accessory.ItemNo, label: accessory.Name }));
  }

  // Each filter (and the final accessory picker) is its own .dtp-field,
  // matching createDropDownField's markup, so they stack via the shared
  // section/field layout rules.
  (field.Filters || []).forEach((filter, index) => {
    const filterContainer = document.createElement('div');
    filterContainer.classList.add('dtp-field');
    sectionContainer.appendChild(filterContainer);

    const filterInputName = inputName + '-filter-' + index;
    const filterLabelId = filterInputName + '-label';

    const filterLabel = document.createElement('label');
    filterLabel.id = filterLabelId;
    filterLabel.setAttribute('for', filterInputName + '-trigger');
    filterContainer.appendChild(filterLabel);
    setLabelText(filterLabel, { DisplayName: filter.Name, IsRequired: false });

    // Only the filter's Values that actually occur among the fetched
    // accessories' CustomProperties are offered, mirroring the legacy
    // configurator's `validOptions` computation.
    const values = [
      ...new Set(
        allOptions
          .map((accessory) =>
            (accessory.CustomProperties || [])
              .filter((prop) => prop.Name === filter.FilterName)
              .map((prop) => prop.Value),
          )
          .flat(),
      ),
    ];
    const validValues = (filter.Values || []).filter((value) => values.includes(value.Value));
    const filterOptions = validValues.map((value) => ({ id: value.Value, label: value.Text }));

    const filterSelect = createDtpSelect({
      id: filterInputName,
      options: filterOptions,
      placeholder: filter.TextInDropDown,
      labelId: filterLabelId,
    });
    filterContainer.appendChild(filterSelect.container);

    const filterError = createErrorMessageElement(filterSelect.triggerElement, filterInputName);
    filterContainer.appendChild(filterError);

    filterSelect.nativeSelect.addEventListener('change', () => {
      const value = filterSelect.nativeSelect.value;

      filterValues = filterValues.filter((fv) => fv.FilterName !== filter.FilterName);
      if (value) {
        filterValues.push({ FilterName: filter.FilterName, Value: value });
      }

      filteredAccessories = filterValues.length
        ? allOptions.filter((accessory) =>
            filterValues.every((fv) =>
              (accessory.CustomProperties || []).some(
                (prop) => prop.Name === fv.FilterName && prop.Value === fv.Value,
              ),
            ),
          )
        : allOptions;

      renderAccessoryPicker();
    });
  });

  const pickerContainer = document.createElement('div');
  pickerContainer.classList.add('dtp-field');
  sectionContainer.appendChild(pickerContainer);

  const labelId = inputName + '-label';
  const fieldLabel = document.createElement('label');
  fieldLabel.id = labelId;
  fieldLabel.setAttribute('for', inputName + '-trigger');
  pickerContainer.appendChild(fieldLabel);
  setLabelText(fieldLabel, field);

  const selectHost = document.createElement('div');
  pickerContainer.appendChild(selectHost);

  const errorHost = document.createElement('div');
  pickerContainer.appendChild(errorHost);

  // Re-created on every filter change, so the previous instance's
  // outside-click listener must be torn down first - otherwise it would
  // keep accumulating with every filter change.
  let currentAccessoryDtpSelect = null;
  // Tracks the user's current pick across re-renders (filter changes), so
  // it isn't lost unless it no longer matches the active filters.
  let selectedAccessoryId = field.SelectedItem || null;

  function renderAccessoryPicker() {
    const previousSelectedId = currentAccessoryDtpSelect
      ? currentAccessoryDtpSelect.nativeSelect.value
      : selectedAccessoryId;

    currentAccessoryDtpSelect?.destroy();
    selectHost.innerHTML = '';
    errorHost.innerHTML = '';

    const preselectedId = filteredAccessories.some((o) => o.ItemNo === previousSelectedId)
      ? previousSelectedId
      : null;
    selectedAccessoryId = preselectedId;

    const accessoryDtpSelect = createDtpSelect({
      id: inputName + '-accessory',
      options: getAccessoryOptions(filteredAccessories),
      placeholder: field.Name,
      selectedId: preselectedId,
      labelId: labelId,
    });
    currentAccessoryDtpSelect = accessoryDtpSelect;
    accessoryDtpSelect.nativeSelect.setAttribute('data-fieldid', field.Id);
    selectHost.appendChild(accessoryDtpSelect.container);

    accessoryDtpSelect.nativeSelect.addEventListener('change', () => {
      selectedAccessoryId = accessoryDtpSelect.nativeSelect.value || null;
      setFieldError(field, '');
    });

    field.inputs = [accessoryDtpSelect.nativeSelect];

    const accessoryError = createErrorMessageElement(accessoryDtpSelect.triggerElement, inputName);
    errorHost.appendChild(accessoryError);
    field.errorElement = accessoryError;
  }

  renderAccessoryPicker();
  appendHelpText(pickerContainer, field);

  context.fields.push(field);
}

// fields based on Sitecore data
export async function setupFields(fields, container, context) {
  // Order field from SortOrder
  fields.sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : -1));

  const {
    accessoriesTemplateID,
    checkboxTemplateID,
    datepickerTemplateID,
    dropdownTemplateID,
    fieldUploadTemplateID,
    multiselectorTemplateID,
    textFieldTemplateID,
    textareaTemplateID,
    patientInfoTemplateID,
  } = inputTemplateIDs;

  // Awaited one by one so callers (see ServiceFlowConfigurator.setupSection)
  // can await this whole function - including the async createAccessoriesField
  // (which fetches its data) - before deciding whether the section ended up
  // with any rendered fields at all.
  for (const field of fields) {
    switch (field.TemplateId) {
      case textFieldTemplateID:
        createInputTextField(field, container, context);
        break;
      case datepickerTemplateID:
        createInputDateField(field, container, context);
        break;
      case checkboxTemplateID:
        createInputCheckboxField(field, container, context);
        break;
      case textareaTemplateID:
        createInputTextAreaField(field, container, context);
        break;
      case multiselectorTemplateID:
        createCategorizedMultiselectField(field, container, context);
        break;
      case dropdownTemplateID:
        createDropDownField(field, container, context);
        break;
      case fieldUploadTemplateID:
        createFileUploadField(field, container, context);
        break;
      case patientInfoTemplateID:
        createPatientInfoField(field, container, context);
        break;
      case accessoriesTemplateID:
        await createAccessoriesField(field, container, context);
        break;
      default:
        break;
    }
  }
}
